import React, { useRef, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MAX_AUDIO_RECORDING_DURATION_SECONDS as DEFAULT_MAX_AUDIO_RECORDING_DURATION_SECONDS } from '../Utils/audioQueue';

const RecordingDialog = ({
  open,
  recordingElapsed,
  stopRecording,
  waveformHistory = [],
  maxRecordingDurationSeconds = DEFAULT_MAX_AUDIO_RECORDING_DURATION_SECONDS
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const safeMaxRecordingDurationSeconds = Number.isFinite(maxRecordingDurationSeconds) && maxRecordingDurationSeconds > 0
    ? Math.floor(maxRecordingDurationSeconds)
    : DEFAULT_MAX_AUDIO_RECORDING_DURATION_SECONDS;
  const remainingSeconds = Math.max(0, safeMaxRecordingDurationSeconds - (recordingElapsed || 0));

  const formatDuration = (seconds) => {
    const total = Math.max(0, Number(seconds) || 0);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = Math.floor(total % 60);
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!open || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    if (waveformHistory.length === 0) return;

    ctx.save();
    ctx.fillStyle = 'rgba(10, 10, 20, 0.65)';
    ctx.fillRect(0, 0, width, height);

    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
    gradient.addColorStop(0.7, 'rgba(89, 89, 176, 0.6)');
    gradient.addColorStop(1, 'rgba(87, 87, 176, 0.3)');

    const totalBars = waveformHistory.length;
    const barWidth = width / totalBars;
    const gap = Math.min(3, barWidth * 0.22);
    const drawWidth = Math.max(2, barWidth - gap);
    const mid = height / 2;
    waveformHistory.forEach((value, index) => {
      const amplitude = Math.min(1, Math.max(0, value || 0));
      const barHeight = amplitude * height * 0.85;
      const x = index * barWidth + gap / 2;
      const y = mid - barHeight / 2;
      ctx.fillStyle = gradient;
      ctx.fillRect(x, y, drawWidth, barHeight);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
      ctx.lineWidth = 1;
      ctx.strokeRect(x, y, drawWidth, barHeight);
    });

    ctx.beginPath();
    ctx.moveTo(0, mid);
    ctx.lineTo(width, mid);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
    ctx.lineWidth = 0.8;
    ctx.setLineDash([6, 4]);
    ctx.stroke();
    ctx.restore();
  }, [open, waveformHistory]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      disableEnforceFocus
      fullWidth
      maxWidth="xs"
      sx={{ zIndex: 2605 }}
      PaperProps={{
        sx: {
          zIndex: 2601,
          borderRadius: 3,
          p: 3,
          m: 0,
          pointerEvents: 'auto',
        },
      }}
      BackdropProps={{
        sx: {
          bgcolor: 'rgba(9, 9, 15, 0.75)',
          zIndex: 2600,
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>
        {t('dashboard.recording_in_progress', 'Recording in progress')}
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
        <Typography variant="body2" sx={{ color: '#e4e4f5' }}>
          {t(
            'dashboard.recording_blocking_message',
            'You cannot interact with other parts of the app while recording. Please finish or stop the recording first.'
          )}
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ color: '#cbd5f5', display: 'block' }}>
            {t('dashboard.recording_elapsed', 'Elapsed')}: {formatDuration(recordingElapsed)}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: remainingSeconds <= 300 ? '#fecaca' : '#cbd5f5',
              display: 'block',
              fontWeight: remainingSeconds <= 300 ? 700 : 500,
            }}
          >
            {t('dashboard.recording_time_remaining', 'Time remaining')}: {formatDuration(remainingSeconds)}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.5 }}>
            {t('dashboard.recording_limit_notice', 'Auto-stop at {{duration}}', {
              duration: formatDuration(safeMaxRecordingDurationSeconds)
            })}
          </Typography>
        </Box>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <canvas
            ref={canvasRef}
            width={200}
            height={60}
            style={{
              border: '1px solid #5757b0',
              borderRadius: '4px',
              backgroundColor: '#f0f0f0',
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          variant="contained"
          color="error"
          onClick={stopRecording}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            minWidth: 140,
          }}
        >
          {t('dashboard.stop_recording', 'Stop recording')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordingDialog;
